/*
  <glimot-recommendations>: shared by "Related to your search", "Recommended for your cart",
  "Complete the set" and "You might also like".

  Source modes (data-source): "auto" | "manual" | "auto_fill" (automatic, then manual products fill
  any remaining slots up to data-limit, in the order arranged in the editor).
  Building the list: automatic results per basis product are taken in turn, duplicates removed,
  exclusions applied (unavailable, in cart, current product, visible search results, products shown
  in earlier rows), then manual products fill remaining slots. Empty → the row stays hidden.

  Performance: nothing is fetched until the row nears the viewport; each basis product + intent is
  fetched once per page and cached in memory; the cart row only refetches when the set of distinct
  products in the cart changes (quantity changes don't trigger requests).
  Suggestions come from Shopify's product relationships, not from the shopper.
*/
(() => {
  if (customElements.get('glimot-recommendations')) return;

  const cache = new Map(); // key: `${productId}|${intent}` -> Promise<string[]> (card HTML)
  let uid = 0;

  const csv = (value) =>
    (value || '')
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);

  function fetchCards(baseUrl, productId, intent, limit) {
    const key = `${productId}|${intent}`;
    if (cache.has(key)) return cache.get(key);
    const url = `${baseUrl}?product_id=${encodeURIComponent(productId)}&limit=${limit}&intent=${intent}&section_id=glimot-reco-data`;
    const promise = fetch(url)
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        if (!html) return [];
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return [...doc.querySelectorAll('[data-g-reco-item]')].map((node) => node.outerHTML);
      })
      .catch(() => []);
    cache.set(key, promise);
    return promise;
  }

  // Recently viewed: one card per product, fetched from the product's own URL (live price/stock).
  function fetchProductCard(handle) {
    const key = `recent|${handle}`;
    if (cache.has(key)) return cache.get(key);
    const root = window.Shopify?.routes?.root || '/';
    const promise = fetch(`${root}products/${encodeURIComponent(handle)}?section_id=glimot-recent-data`)
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        if (!html) return [];
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return [...doc.querySelectorAll('[data-g-reco-item]')].map((node) => node.outerHTML);
      })
      .catch(() => []);
    cache.set(key, promise);
    return promise;
  }

  const productIdOf = (html) => {
    const match = /data-product-id="(\d+)"/.exec(html);
    return match ? match[1] : null;
  };

  class GlimotRecommendations extends HTMLElement {
    connectedCallback() {
      this.uid = ++uid;
      this.context = this.dataset.context || 'product';
      this.source = this.dataset.source || 'auto_fill';
      this.intents = csv(this.dataset.intents || 'related');
      this.limit = Math.max(1, parseInt(this.dataset.limit, 10) || 4);
      this.basisLimit = Math.max(1, parseInt(this.dataset.basisLimit, 10) || 1);
      this.excludeIds = new Set(csv(this.dataset.excludeIds));
      this.cartIds = new Set(csv(this.dataset.cartIds));
      this.excludeCart = this.dataset.excludeCart !== 'false';
      this.noRepeat = this.dataset.noRepeat === 'true';
      this.showAtc = this.dataset.showAtc !== 'false';
      this.baseUrl = this.dataset.recommendationsUrl || '/recommendations/products';
      this.list = this.querySelector('[data-g-reco-list]');
      this.addAll = this.querySelector('[data-g-reco-add-all]');
      this.status = this.querySelector('[data-g-reco-status]');
      this.manual = [...(this.querySelector('template[data-g-manual]')?.content.querySelectorAll('[data-g-reco-item]') || [])].map(
        (node) => node.outerHTML
      );
      this.automatic = [];
      this.shownIds = [];
      GlimotRecommendations.rows.add(this);

      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));

      if (this.context === 'recent') {
        this.source = 'manual';
        this.excludeCart = false;
        this.basisIds = [];
      } else if (this.context === 'cart') {
        this.basisIds = this.cartBasisFromDom();
        this.cartIds = new Set(this.cartProductIdsFromDom());
        if (typeof subscribe === 'function') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
            window.clearTimeout(this.cartTimer);
            this.cartTimer = window.setTimeout(() => this.onCartChanged(), 300);
          });
        }
      } else {
        this.basisIds = csv(this.dataset.basisIds).slice(0, this.basisLimit);
      }

      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          this.load();
        },
        { rootMargin: '400px 0px' }
      );
      // The row stays `hidden` until it has products, and hidden elements never intersect, so
      // watch the section wrapper (always in the layout, even at zero height) instead.
      observer.observe(this.parentElement || this);
    }

    disconnectedCallback() {
      GlimotRecommendations.rows.delete(this);
      if (this.unsubscribe) this.unsubscribe();
    }

    cartProductIdsFromDom() {
      const ids = [...document.querySelectorAll('#main-cart-items [data-g-cart-product]')].map((n) => n.dataset.gCartProduct);
      return [...new Set(ids)];
    }

    cartBasisFromDom() {
      return this.cartProductIdsFromDom().slice(0, this.basisLimit);
    }

    onCartChanged() {
      const ids = this.cartProductIdsFromDom();
      this.cartIds = new Set(ids);
      const basis = ids.slice(0, this.basisLimit);
      const changed = basis.join(',') !== (this.basisIds || []).join(',');
      this.basisIds = basis;
      if (!ids.length) {
        this.render([]);
        return;
      }
      if (changed) this.load();
      else this.render();
    }

    // Recently viewed reads only product ids/handles saved on this device (glimot.recent);
    // nothing about the shopper is stored or sent.
    async loadRecent() {
      let list = [];
      try {
        list = JSON.parse(window.localStorage.getItem('glimot.recent') || '[]');
      } catch (e) {
        list = [];
      }
      const handles = list
        .filter((item) => item && item.handle && !this.excludeIds.has(String(item.id)))
        .slice(0, this.limit + 2)
        .map((item) => String(item.handle));
      const cards = await Promise.all(handles.map(fetchProductCard));
      this.manual = cards.flat();
      this.render();
    }

    async load() {
      if (this.context === 'recent') return this.loadRecent();
      if (this.source !== 'manual' && this.basisIds.length) {
        const fetchLimit = Math.min(10, this.limit + 4);
        const perBasis = await Promise.all(
          this.basisIds.map(async (id) => {
            const collected = [];
            for (const intent of this.intents) {
              const cards = await fetchCards(this.baseUrl, id, intent, fetchLimit);
              collected.push(...cards);
              // Only ask for the next intent (e.g. related after complementary) if still short.
              if (this.eligible(collected).length >= this.limit) break;
            }
            return collected;
          })
        );
        // Take results in turn from each basis product so each one contributes.
        const merged = [];
        const longest = Math.max(0, ...perBasis.map((list) => list.length));
        for (let i = 0; i < longest; i++) {
          perBasis.forEach((list) => {
            if (list[i]) merged.push(list[i]);
          });
        }
        this.automatic = merged;
      } else {
        this.automatic = [];
      }
      this.render();
    }

    excludedByEarlierRows() {
      if (!this.noRepeat) return new Set();
      const ids = new Set();
      GlimotRecommendations.rows.forEach((row) => {
        if (row === this) return;
        if (row.compareDocumentPosition(this) & Node.DOCUMENT_POSITION_FOLLOWING) {
          row.shownIds.forEach((id) => ids.add(id));
        }
      });
      return ids;
    }

    eligible(candidates) {
      const seen = new Set();
      const earlier = this.excludedByEarlierRows();
      return candidates.filter((html) => {
        const id = productIdOf(html);
        if (!id || seen.has(id)) return false;
        seen.add(id);
        if (html.includes('data-available="false"')) return false;
        if (this.excludeIds.has(id)) return false;
        if (this.excludeCart && this.cartIds.has(id)) return false;
        if (earlier.has(id)) return false;
        return true;
      });
    }

    compute() {
      let pool = [];
      if (this.source === 'auto') pool = this.automatic;
      else if (this.source === 'manual') pool = this.manual;
      else pool = [...this.automatic, ...this.manual];
      return this.eligible(pool).slice(0, this.limit);
    }

    render(forced) {
      const cards = forced || this.compute();
      this.shownIds = cards.map(productIdOf);
      if (!this.list) return;
      if (!cards.length) {
        this.list.replaceChildren();
        this.hidden = true;
        return;
      }
      const html = cards
        .map((card) => card.replace(/id="GReco-(\d+)-[^"]*"/g, `id="GReco-$1-r${this.uid}"`).replace(/for="GReco-(\d+)-[^"]*"/g, `for="GReco-$1-r${this.uid}"`))
        .join('');
      this.list.innerHTML = html;
      if (!this.showAtc) this.list.querySelectorAll('[data-g-reco-buy]').forEach((node) => node.remove());
      this.hidden = false;
      this.updateAddAll();
      // Rows after this one may need to drop products now shown here.
      GlimotRecommendations.rows.forEach((row) => {
        if (row !== this && row.noRepeat && row.automatic && (this.compareDocumentPosition(row) & Node.DOCUMENT_POSITION_FOLLOWING)) {
          if (row.list && row.list.childElementCount) row.render();
        }
      });
    }

    onChange(event) {
      const select = event.target.closest('[data-g-reco-select]');
      if (!select) return;
      const button = select.closest('[data-g-reco-buy]')?.querySelector('[data-g-reco-add]');
      if (!button) return;
      const ok = !!select.value;
      button.disabled = !ok;
      button.setAttribute('aria-disabled', String(!ok));
      this.updateAddAll();
    }

    // "Add all" works only when every shown item has a valid option chosen and none needs
    // required personalisation (those cards show a "Personalise" link instead of a button).
    readyItems() {
      const cards = [...(this.list?.querySelectorAll('[data-g-reco-item]') || [])];
      const ready = cards.map((card) => {
        const button = card.querySelector('[data-g-reco-add]');
        if (!button) return null;
        const variantId = button.dataset.variantId || card.querySelector('[data-g-reco-select]')?.value;
        return variantId ? { card, variantId } : null;
      });
      return cards.length > 1 && ready.every(Boolean) ? ready : null;
    }

    updateAddAll() {
      if (!this.addAll) return;
      const cards = this.list?.querySelectorAll('[data-g-reco-item]') || [];
      this.addAll.closest('[data-g-reco-add-all-wrap]').hidden = !this.showAtc || cards.length < 2;
      const ok = !!this.readyItems();
      this.addAll.disabled = !ok;
      this.addAll.setAttribute('aria-disabled', String(!ok));
    }

    async onAddAll() {
      const items = this.readyItems();
      if (!items) return;
      const strings = window.glimot?.strings || {};
      const label = this.addAll.innerHTML;
      this.addAll.disabled = true;
      this.addAll.setAttribute('aria-busy', 'true');
      this.addAll.textContent = strings.adding || 'Adding…';
      let result = { ok: true };
      for (let i = 0; i < items.length; i++) {
        result = await window.Glimot.addToCart(
          { id: Number(items[i].variantId), quantity: 1 },
          { openCart: i === items.length - 1 && this.context !== 'cart' }
        );
        if (!result.ok) break;
        if (items[i].card.dataset.productId) this.cartIds.add(items[i].card.dataset.productId);
      }
      this.addAll.removeAttribute('aria-busy');
      this.addAll.innerHTML = label;
      if (!result.ok) {
        // Items added before the failure stay in the cart; say so plainly.
        this.status?.replaceChildren();
        const p = document.createElement('p');
        p.className = 'g-message g-message--error';
        p.textContent = result.message || strings.error || '';
        this.status?.append(p);
        this.updateAddAll();
        return;
      }
      window.Glimot.announce(strings.added || 'Added to cart');
      this.showStatus(strings.added || 'Added to cart', result.cartType === 'none');
      if (this.excludeCart) this.render();
      else this.updateAddAll();
    }

    onClick(event) {
      if (event.target.closest('[data-g-reco-add-all]')) {
        this.onAddAll();
        return;
      }
      const button = event.target.closest('[data-g-reco-add]');
      if (!button || !this.contains(button)) return;
      const card = button.closest('[data-g-reco-item]');
      const buy = button.closest('[data-g-reco-buy]');
      const select = buy?.querySelector('[data-g-reco-select]');
      const message = buy?.querySelector('[data-g-reco-message]');
      const variantId = button.dataset.variantId || select?.value;
      const strings = window.glimot?.strings || {};
      if (!variantId) {
        // Never add a default option silently: ask for a choice.
        select?.focus();
        return;
      }
      const label = button.innerHTML;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = strings.adding || 'Adding…';
      if (message) {
        message.className = 'g-message';
        message.textContent = '';
      }
      window.Glimot.addToCart({ id: Number(variantId), quantity: 1 }, { openCart: this.context !== 'cart' }).then((result) => {
        button.removeAttribute('aria-busy');
        button.innerHTML = label;
        button.disabled = false;
        if (!result.ok) {
          if (message) {
            message.className = 'g-message g-message--error';
            message.textContent = result.message || strings.error || '';
          }
          return;
        }
        const added = strings.added || 'Added to cart';
        window.Glimot.announce(added);
        this.showStatus(added, result.cartType === 'none');
        const productId = card?.dataset.productId;
        if (productId) this.cartIds.add(productId);
        if (this.excludeCart) this.render();
      });
    }

    showStatus(text, withViewCart) {
      if (!this.status) return;
      this.status.replaceChildren();
      const p = document.createElement('p');
      p.className = 'g-message g-message--success';
      p.textContent = text;
      if (withViewCart) {
        const link = document.createElement('a');
        link.href = window.glimot?.routes?.cart || '/cart';
        link.className = 'g-link';
        link.textContent = window.glimot?.strings?.viewCart || 'View cart';
        p.append(' ', link);
      }
      this.status.append(p);
    }
  }

  GlimotRecommendations.rows = new Set();
  customElements.define('glimot-recommendations', GlimotRecommendations);
})();
